import { DI, resolve } from 'aurelia';
import { doc, onSnapshot } from 'firebase/firestore';
import { IFirebase } from './firebase';
import { IClock } from './clock';
import { IConfig } from './config';
import type { DrawPhase, LatestDraw, LiveState, OpenGame, RecentDraw } from '@shared/index';

/**
 * Live view of the game. Subscribes to the one live-state doc (open game,
 * latest result, recent results) and runs the reveal timeline off the server clock so
 * every screen shows the same ball at the same moment.
 */
export interface IDraws {
  open: OpenGame | null;
  latest: LatestDraw | null;
  recent: RecentDraw[];
  /** seed-chain anchor currently in use */
  anchorId: string | null;
  /** how many of `latest.numbers` are visible right now (0..20) */
  revealed: number;
  /** what the board is doing */
  phase: DrawPhase;
  /** ms until the open game closes for tickets */
  msToClose: number;
  /** ms until the open game is drawn */
  msToDraw: number;
  /** seconds, whole, until close (for the countdown) */
  secondsToClose: number;
  revealDone: boolean;
  start(): void;
  stop(): void;
  /** call when a frame needs a fresh snapshot of time-derived fields */
  tick(): void;
}

export const IDraws = DI.createInterface<IDraws>('IDraws', (x) => x.singleton(DrawsService));

export class DrawsService implements IDraws {
  open: OpenGame | null = null;
  latest: LatestDraw | null = null;
  recent: RecentDraw[] = [];
  anchorId: string | null = null;
  revealed = 0;
  phase: DrawPhase = 'open';
  msToClose = 0;
  msToDraw = 0;
  secondsToClose = 0;
  revealDone = false;

  private fb = resolve(IFirebase);
  private clock = resolve(IClock);
  private config = resolve(IConfig);
  private unsubs: (() => void)[] = [];
  private timer: number | null = null;
  private lastKick = 0;
  private kicking = false;

  start(): void {
    if (this.unsubs.length) return;
    this.unsubs.push(onSnapshot(doc(this.fb.db, 'config', 'live'), (snap) => {
      const live = snap.data() as LiveState | undefined;
      this.open = live?.open ?? null;
      this.latest = live?.latest ?? null;
      this.recent = live?.recent ?? [];
      this.anchorId = live?.anchorId ?? null;
      this.tick();
    }, (e) => console.error('live state subscription', e)));
    this.timer = window.setInterval(() => this.tick(), 250);
    void this.kick();
  }

  stop(): void {
    this.unsubs.forEach((u) => u());
    this.unsubs = [];
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  tick(): void {
    const now = this.clock.now();
    const interval = this.config.game.revealIntervalMs;

    if (this.latest?.numbers && this.latest.drawnAt) {
      const elapsed = now - this.latest.drawnAt;
      const n = Math.floor(elapsed / interval) + 1;
      this.revealed = Math.max(0, Math.min(20, n));
    } else {
      this.revealed = 0;
    }
    this.revealDone = this.revealed >= 20;

    if (this.open) {
      this.msToClose = Math.max(0, this.open.closesAt - now);
      this.msToDraw = Math.max(0, this.open.drawsAt - now);
      this.secondsToClose = Math.ceil(this.msToClose / 1000);
    } else {
      this.msToClose = this.msToDraw = this.secondsToClose = 0;
    }

    if (this.latest && !this.revealDone) this.phase = 'revealing';
    else if (this.open && now < this.open.closesAt) this.phase = 'open';
    else if (this.open) this.phase = 'closed';
    else this.phase = 'settled';

    // Overdue draw (scheduler late, or running on the emulator): poke the server.
    const overdue = this.open ? now - this.open.drawsAt : 0;
    if ((overdue > 3000 || !this.open) && now - this.lastKick > 5000) void this.kick();
  }

  private async kick(): Promise<void> {
    if (this.kicking) return;
    this.kicking = true;
    this.lastKick = this.clock.now();
    try { await this.fb.call('tickDraws'); } catch (e) { console.warn('tickDraws failed', e); }
    finally { this.kicking = false; }
  }
}
